import { useState, useEffect, useCallback } from 'react';
import { useOrgEmployeeApi } from '@shared/services';
import type { OrgEmployee } from '@shared/types';

/**
 * Hook to fetch organization employee departments data
 * Returns the org employee data with their departments/employees
 */
export function useOrgEmployeeDepartments(): {
    orgEmployee: OrgEmployee | undefined;
    isLoading: boolean;
    error: string | undefined;
    refetch: () => Promise<void>;
} {
    const [orgEmployee, setOrgEmployee] = useState<OrgEmployee | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>();

    const { get: getOrgEmployee } = useOrgEmployeeApi();

    const fetchData = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        setError(undefined);
        try {
            const data = await getOrgEmployee();
            setOrgEmployee(data);
        } catch (err) {
            // Authentication errors are handled by ProtectedRoute
            // Only show other errors
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            if (
                !errorMessage.includes('Not authenticated') &&
                !errorMessage.includes('Unauthorized') &&
                !errorMessage.includes('login_required')
            ) {
                setError('Kunne ikke laste avdelingsdata');
                console.error('Failed to fetch org-employee:', err);
            }
        } finally {
            setIsLoading(false);
        }
    }, [getOrgEmployee]);

    useEffect((): void => {
        fetchData();
    }, [fetchData]);

    return {
        orgEmployee,
        isLoading,
        error,
        refetch: fetchData,
    };
}
